/**
 * Semantic Color Tokens
 * Maps base colors to semantic meanings (primary, secondary, error, etc.)
 */

import { baseColors } from '../base/colors';

export const semanticColors = {
  // Primary brand color - Atlas green scale
  primary: {
    main: baseColors.atlas.primary500,   // #2F7A6D - Buttons/Active
    light: baseColors.atlas.primary600,  // #256158 - Hover
    dark: baseColors.atlas.primary700,   // #1F4D45 - Header
    contrastText: baseColors.white,
    soft: baseColors.atlas.primary100,   // #E7F3F0 - Soft background
  },

  // Secondary brand color
  secondary: {
    main: baseColors.gray[500],       // #505050
    light: baseColors.gray[300],
    dark: baseColors.gray[700],
    contrastText: baseColors.white,
  },

  // Error/danger states
  error: {
    main: baseColors.atlas.error,     // #D64545
    light: '#E57373',
    dark: '#C62828',
    contrastText: baseColors.white,
  },

  // Warning states
  warning: {
    main: baseColors.orange[500],
    light: baseColors.orange[300],
    dark: baseColors.orange[700],
    contrastText: baseColors.neutral[900],
  },

  // Info states
  info: {
    main: baseColors.blue[500],       // #2196F3 (Fluent 2 standard - was yellow)
    light: baseColors.blue[300],      // #64B5F6
    dark: baseColors.blue[700],       // #1976D2
    contrastText: baseColors.white,   // White text for contrast
  },

  // Success states
  success: {
    main: baseColors.green[600],
    light: baseColors.green[300],
    dark: baseColors.green[800],
    contrastText: baseColors.white,
  },

  // Background colors (light mode) - Atlas palette
  background: {
    default: baseColors.atlas.background,  // #F5F7F6 - Page background
    paper: baseColors.atlas.cardBg,        // #FFFFFF - Card/surface background
    elevated: baseColors.white,            // Elevated surfaces (dialogs, menus)
  },

  // Text colors (light mode) - Atlas palette
  text: {
    primary: baseColors.atlas.textPrimary,   // #2B2F2E
    secondary: baseColors.atlas.textSecondary, // #6B7472
    disabled: 'rgba(0, 0, 0, 0.38)',
  },

  // Divider - Atlas border
  divider: baseColors.atlas.border,  // #E3E6E5

  // Action colors (buttons, links, etc.)
  action: {
    active: 'rgba(0, 0, 0, 0.54)',
    hover: 'rgba(0, 0, 0, 0.04)',
    hoverOpacity: 0.04,
    selected: 'rgba(0, 0, 0, 0.08)',
    selectedOpacity: 0.08,
    disabled: 'rgba(0, 0, 0, 0.26)',
    disabledBackground: 'rgba(0, 0, 0, 0.12)',
    disabledOpacity: 0.38,
    focus: 'rgba(0, 0, 0, 0.12)',
    focusOpacity: 0.12,
    activatedOpacity: 0.12,
    // Atlas action button color (accent yellow)
    primary: baseColors.atlas.accent500,      // #E5B100
    primaryHover: baseColors.atlas.accent400, // #F2C94C
    primarySoft: baseColors.atlas.accent100,  // #FFF6D6
    primaryContrastText: baseColors.atlas.primary700,  // Dark text on gold
  },

  // Atlas-specific semantic colors
  atlas: {
    accent: baseColors.atlas.accent500,
    navigation: {
      background: baseColors.atlas.primary700,           // Header (#1F4D45)
      secondaryBackground: baseColors.atlas.primary600,  // Module nav (#256158)
      text: baseColors.white,
      textInactive: 'rgba(255, 255, 255, 0.7)',
      activeIndicator: baseColors.atlas.accent500,       // Gold underline (#E5B100)
      ghostButtonBorder: 'rgba(255, 255, 255, 0.3)',
    },
    status: {
      active: baseColors.atlas.primary500,       // #2F7A6D
      activeBg: baseColors.atlas.primary100,     // #E7F3F0
      expiring: baseColors.atlas.accent500,      // #E5B100
      expiringBg: baseColors.atlas.accent100,    // #FFF6D6
      historical: baseColors.atlas.textSecondary, // #6B7472
      historicalBg: baseColors.atlas.neutralBadge, // #DDE3E1
    },
    surface: {
      card: baseColors.atlas.cardBg,
      cardBorder: baseColors.atlas.border,
      subtle: baseColors.table.headerBg,
    },
    // Chart colors
    chart: {
      periods: ['#076B69', '#61AAA4', '#66A3D4', '#E9C63C', '#E8CFA3', '#CFD1D3'],
      meters: ['#2F7A6D', '#8A6500', '#1565C0', '#7A4E00', '#00695C', '#616161', '#5E35B1', '#C62828', '#2E7D32', '#455A64'],
      gas: baseColors.atlas.accent500,
      electricity: '#2F7A6D',
      grid: baseColors.chart.grid,
      trendLine: baseColors.chart.trendLine,
      cursor: baseColors.atlas.accent100,
    },
    // Table colors
    table: {
      headerBg: baseColors.table.headerBg,
      headerText: '#626A68',
      rowAlt: baseColors.table.rowAlt,
      rowHover: baseColors.table.rowHover,
      border: baseColors.table.border,
    },
  },
} as const;

// Dark mode semantic colors
export const semanticColorsDark = {
  primary: {
    main: baseColors.green[300],      // Lighter green for dark mode
    light: baseColors.green[200],
    dark: baseColors.green[500],
    contrastText: baseColors.neutral[900],
  },

  secondary: {
    main: baseColors.gray[400],
    light: baseColors.gray[200],
    dark: baseColors.gray[600],
    contrastText: baseColors.neutral[900],
  },

  error: {
    main: baseColors.red[400],
    light: baseColors.red[300],
    dark: baseColors.red[600],
    contrastText: baseColors.white,
  },

  warning: {
    main: baseColors.orange[400],
    light: baseColors.orange[300],
    dark: baseColors.orange[600],
    contrastText: baseColors.neutral[900],
  },

  info: {
    main: baseColors.blue[400],       // #42A5F5 (lighter for dark mode)
    light: baseColors.blue[300],      // #64B5F6
    dark: baseColors.blue[600],       // #1E88E5
    contrastText: baseColors.neutral[900],
  },

  success: {
    main: baseColors.green[400],
    light: baseColors.green[300],
    dark: baseColors.green[700],
    contrastText: baseColors.neutral[900],
  },

  background: {
    default: '#121212',
    paper: '#1E1E1E',
    elevated: '#242424',
  },

  text: {
    primary: 'rgba(255, 255, 255, 0.87)',
    secondary: 'rgba(255, 255, 255, 0.6)',
    disabled: 'rgba(255, 255, 255, 0.38)',
  },

  divider: 'rgba(255, 255, 255, 0.12)',

  action: {
    active: 'rgba(255, 255, 255, 0.56)',
    hover: 'rgba(255, 255, 255, 0.08)',
    hoverOpacity: 0.08,
    selected: 'rgba(255, 255, 255, 0.16)',
    selectedOpacity: 0.16,
    disabled: 'rgba(255, 255, 255, 0.3)',
    disabledBackground: 'rgba(255, 255, 255, 0.12)',
    disabledOpacity: 0.38,
    focus: 'rgba(255, 255, 255, 0.12)',
    focusOpacity: 0.12,
    activatedOpacity: 0.24,
  },

  atlas: {
    accent: baseColors.atlas.accent400,
    navigation: {
      background: baseColors.atlas.primary700,
      secondaryBackground: baseColors.atlas.primary600,
      text: baseColors.white,
      textInactive: 'rgba(255, 255, 255, 0.7)',
      activeIndicator: baseColors.atlas.accent400,
      ghostButtonBorder: 'rgba(255, 255, 255, 0.4)',
    },
    status: {
      active: '#81C784',
      activeBg: '#263D35',
      expiring: '#F2C94C',
      expiringBg: '#433A1D',
      historical: '#BDBDBD',
      historicalBg: '#343837',
    },
    surface: {
      card: '#1E1E1E',
      cardBorder: '#666666',
      subtle: '#2A2F2D',
    },
    chart: {
      periods: ['#7FB7AD', '#81C784', '#90CAF9', '#F2C94C', '#FFB74D', '#BDBDBD'],
      meters: ['#7FB7AD', '#F2C94C', '#90CAF9', '#FFB74D', '#81C784', '#BDBDBD', '#B39DDB', '#EF9A9A', '#A5D6A7', '#90A4AE'],
      gas: '#F2C94C',
      electricity: '#7FB7AD',
      grid: '#666666',
      trendLine: '#7FB7AD',
      cursor: '#433A1D',
    },
    table: {
      headerBg: '#2A2F2D',
      headerText: '#A5A5A5',
      rowAlt: '#242826',
      rowHover: '#303633',
      border: '#666666',
    },
  },
} as const;

export type SemanticColors = typeof semanticColors;
export type SemanticColorsDark = typeof semanticColorsDark;
